import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // Nav
    properties: 'Properties', services: 'Services', education: 'Education',
    media: 'Media', our_team: 'Our Team', empire_group: 'Empire Group',
    sign_in: 'Sign In', register: 'Register', admin_access: 'Admin Access',
    back: 'Back', back_to_home: 'Back to Home', back_to_listings: 'Back to Listings',
    logout: 'Logout',

    // Hero
    hero_overline: '01 — Welcome',
    hero_title_1: 'Premium Property', hero_title_2: 'Investment',
    hero_subtitle: 'Residential Sales, Luxury Rentals, Land Acquisition & Hotel Development Partnerships across Thailand.',
    explore_properties: 'Explore Properties', meet_our_team: 'Meet Our Team',

    // Stats
    stat_properties: 'Premium Properties', stat_cities: 'Key Locations',
    stat_clients: 'Happy Clients', stat_experience: 'Years Experience',

    // Featured
    featured_overline: '02 — Featured Listings', featured_title: 'Exclusive Properties',

    // About
    about_overline: '03 — About NOK',
    about_title: 'Your Trusted Partner in Thailand Real Estate',
    about_desc: "We provide expert guidance for property acquisition, legal services, insurance, and wealth protection. Whether you're purchasing a luxury villa, investing in commercial property, or planning your retirement in Thailand, our team delivers exceptional service.",
    about_item_1: 'Residential Sales & Luxury Rentals', about_item_2: 'Land Acquisition & Development',
    about_item_3: 'Insurance & Wealth Protection', about_item_4: 'Legal Advisory for Foreign Investors',
    view_our_services: 'View Our Services',

    // Properties section
    all_properties_overline: '04 — All Properties', browse_collection: 'Browse Our Collection',
    all_types: 'All Types', all_listings: 'All Listings',
    house: 'House', condo: 'Condo', land: 'Land', commercial: 'Commercial', retail: 'Retail',
    sale: 'Sale', rental: 'Rental', search_city: 'Search city...',
    view: 'View', no_properties: 'No properties match your criteria',
    price: 'Price', location: 'Location', bedrooms: 'Bedrooms', bathrooms: 'Bathrooms',
    size: 'Size', square_meters: 'Square Meters', status: 'Status',
    property_type: 'Property Type', listing_type: 'Listing Type',

    // Footer
    footer_desc: "Thailand's premium real estate and investment platform. Expert guidance for property acquisition, legal services, and wealth protection.",
    quick_links: 'Quick Links', locations: 'Locations', contact: 'Contact',
    register_for_updates: 'Register for Updates',
    all_rights: 'All rights reserved.',

    // Property Detail
    contact_us: 'Contact Us', line_pook: 'LINE — Pook',
    interested_msg: 'Interested in this property? Contact NOK Thailand for more information.',
    description: 'Description', amenities: 'Amenities',

    // Team Page
    our_professionals: 'Our Professionals', meet_our_team_title: 'Meet Our Team',
    team_desc: 'Expert professionals dedicated to providing comprehensive real estate, legal, and financial services for investors and retirees in Thailand.',
    credentials: 'Credentials', expertise: 'Expertise', specialty: 'Specialty',
    line_official: 'LINE Official Account',

    // Services Page
    our_services: 'Our Services', services_title: 'Professional Services',
    services_desc: 'Comprehensive real estate, legal, insurance, and financial services tailored for foreign investors and retirees in Thailand.',

    // Education Page
    knowledge_hub: 'Knowledge Hub', education_title: 'Education & Insights',
    education_desc: 'Expert articles and guides on Thai real estate, legal matters, insurance, and investment strategies.',
    all_topics: 'All Topics', read_more: 'Read More', featured: 'Featured',
    featured_articles: 'Featured Articles',

    // Empire Group
    empire_overline: 'Authorized Agent',
    empire_title: 'Empire Group Thailand',
    empire_desc: 'As an authorized agent of Empire Group, one of Thailand\'s premier property developers, we offer exclusive access to their prestigious developments.',
    interested_empire: 'Interested in Empire Group Properties?',
    empire_cta_desc: 'As an authorized agent, we can provide exclusive access to Empire Group\'s latest developments, special pricing, and personalized consultation for your property investment needs.',
    contact_our_team: 'Contact Our Team', visit_empire: 'Visit Empire Group Website',

    // Media
    media_gallery: 'Media Gallery', media_desc: 'Photos and highlights from our property launches, team events, and featured listings.',

    // Translate buttons
    translate_thai: 'ไทย', translate_chinese: '中文', translate_russian: 'Русский',
  },
  th: {
    properties: 'อสังหาริมทรัพย์', services: 'บริการ', education: 'การศึกษา',
    media: 'มีเดีย', our_team: 'ทีมงาน', empire_group: 'เอ็มไพร์ กรุ๊ป',
    sign_in: 'เข้าสู่ระบบ', register: 'ลงทะเบียน', admin_access: 'ผู้ดูแลระบบ',
    back: 'กลับ', back_to_home: 'กลับหน้าแรก', back_to_listings: 'กลับรายการ',
    logout: 'ออกจากระบบ',

    hero_overline: '01 — ยินดีต้อนรับ',
    hero_title_1: 'อสังหาริมทรัพย์ระดับพรีเมียม', hero_title_2: 'การลงทุน',
    hero_subtitle: 'ขายที่อยู่อาศัย เช่าหรู ซื้อที่ดิน และพัฒนาโรงแรมทั่วประเทศไทย',
    explore_properties: 'สำรวจอสังหาริมทรัพย์', meet_our_team: 'พบกับทีมงาน',

    stat_properties: 'อสังหาริมทรัพย์พรีเมียม', stat_cities: 'สถานที่สำคัญ',
    stat_clients: 'ลูกค้าที่พอใจ', stat_experience: 'ปีประสบการณ์',

    featured_overline: '02 — รายการแนะนำ', featured_title: 'อสังหาริมทรัพย์พิเศษ',

    about_overline: '03 — เกี่ยวกับ NOK',
    about_title: 'พันธมิตรที่คุณไว้วางใจในอสังหาริมทรัพย์ไทย',
    about_desc: 'เราให้คำแนะนำจากผู้เชี่ยวชาญสำหรับการซื้ออสังหาริมทรัพย์ บริการด้านกฎหมาย ประกันภัย และการปกป้องทรัพย์สิน',
    about_item_1: 'ขายบ้านและเช่าหรู', about_item_2: 'การจัดหาที่ดินและพัฒนา',
    about_item_3: 'ประกันภัยและการปกป้องทรัพย์สิน', about_item_4: 'ที่ปรึกษาด้านกฎหมายสำหรับนักลงทุนต่างชาติ',
    view_our_services: 'ดูบริการของเรา',

    all_properties_overline: '04 — อสังหาริมทรัพย์ทั้งหมด', browse_collection: 'เรียกดูคอลเลกชัน',
    all_types: 'ทุกประเภท', all_listings: 'ทุกรายการ',
    house: 'บ้าน', condo: 'คอนโด', land: 'ที่ดิน', commercial: 'พาณิชย์', retail: 'ร้านค้า',
    sale: 'ขาย', rental: 'เช่า', search_city: 'ค้นหาเมือง...',
    view: 'ดู', no_properties: 'ไม่พบอสังหาริมทรัพย์ที่ตรงกับเกณฑ์',
    price: 'ราคา', location: 'ที่ตั้ง', bedrooms: 'ห้องนอน', bathrooms: 'ห้องน้ำ',
    size: 'ขนาด', square_meters: 'ตร.ม.', status: 'สถานะ',
    property_type: 'ประเภทอสังหาริมทรัพย์', listing_type: 'ประเภทรายการ',

    footer_desc: 'แพลตฟอร์มอสังหาริมทรัพย์และการลงทุนระดับพรีเมียมของไทย',
    quick_links: 'ลิงก์ด่วน', locations: 'สถานที่', contact: 'ติดต่อ',
    register_for_updates: 'ลงทะเบียนรับข่าวสาร',
    all_rights: 'สงวนสิทธิ์ทุกประการ',

    contact_us: 'ติดต่อเรา', line_pook: 'LINE — Pook',
    interested_msg: 'สนใจอสังหาริมทรัพย์นี้? ติดต่อ NOK Thailand เพื่อข้อมูลเพิ่มเติม',
    description: 'รายละเอียด', amenities: 'สิ่งอำนวยความสะดวก',

    our_professionals: 'ผู้เชี่ยวชาญของเรา', meet_our_team_title: 'พบกับทีมงาน',
    team_desc: 'ผู้เชี่ยวชาญที่อุทิศตนในการให้บริการอสังหาริมทรัพย์ กฎหมาย และการเงินที่ครอบคลุม',
    credentials: 'คุณสมบัติ', expertise: 'ความเชี่ยวชาญ', specialty: 'ความชำนาญ',
    line_official: 'LINE Official Account',

    our_services: 'บริการของเรา', services_title: 'บริการมืออาชีพ',
    services_desc: 'บริการอสังหาริมทรัพย์ กฎหมาย ประกันภัย และการเงินอย่างครบวงจร',

    knowledge_hub: 'คลังความรู้', education_title: 'การศึกษาและข้อมูลเชิงลึก',
    education_desc: 'บทความจากผู้เชี่ยวชาญและคู่มือเกี่ยวกับอสังหาริมทรัพย์ กฎหมาย ประกันภัย และกลยุทธ์การลงทุน',
    all_topics: 'ทุกหัวข้อ', read_more: 'อ่านเพิ่มเติม', featured: 'แนะนำ',
    featured_articles: 'บทความแนะนำ',

    empire_overline: 'ตัวแทนที่ได้รับอนุญาต',
    empire_title: 'เอ็มไพร์ กรุ๊ป ไทยแลนด์',
    empire_desc: 'ในฐานะตัวแทนที่ได้รับอนุญาตของ Empire Group เราเปิดให้เข้าถึงโครงการที่มีชื่อเสียง',
    interested_empire: 'สนใจอสังหาริมทรัพย์ Empire Group?',
    empire_cta_desc: 'ในฐานะตัวแทนที่ได้รับอนุญาต เราสามารถให้สิทธิ์พิเศษในการเข้าถึงโครงการล่าสุด ราคาพิเศษ และคำปรึกษาส่วนบุคคล',
    contact_our_team: 'ติดต่อทีมงาน', visit_empire: 'เยี่ยมชมเว็บไซต์ Empire Group',

    media_gallery: 'แกลเลอรีมีเดีย', media_desc: 'ภาพถ่ายและไฮไลท์จากการเปิดตัวอสังหาริมทรัพย์ กิจกรรมของทีม',

    translate_thai: 'ไทย', translate_chinese: '中文', translate_russian: 'Русский',
  },
  zh: {
    properties: '房产', services: '服务', education: '教育',
    media: '媒体', our_team: '我们的团队', empire_group: 'Empire集团',
    sign_in: '登录', register: '注册', admin_access: '管理员',
    back: '返回', back_to_home: '返回首页', back_to_listings: '返回列表',
    logout: '退出',

    hero_overline: '01 — 欢迎',
    hero_title_1: '优质房产', hero_title_2: '投资',
    hero_subtitle: '泰国各地的住宅销售、豪华租赁、土地收购和酒店开发合作。',
    explore_properties: '探索房产', meet_our_team: '认识我们的团队',

    stat_properties: '优质房产', stat_cities: '核心地段',
    stat_clients: '满意客户', stat_experience: '年经验',

    featured_overline: '02 — 精选推荐', featured_title: '独家房产',

    about_overline: '03 — 关于NOK',
    about_title: '您在泰国房地产领域值得信赖的合作伙伴',
    about_desc: '我们提供房产收购、法律服务、保险和财富保护的专业指导。',
    about_item_1: '住宅销售和豪华租赁', about_item_2: '土地收购和开发',
    about_item_3: '保险和财富保护', about_item_4: '外国投资者法律顾问',
    view_our_services: '查看我们的服务',

    all_properties_overline: '04 — 所有房产', browse_collection: '浏览我们的房产',
    all_types: '所有类型', all_listings: '所有列表',
    house: '别墅', condo: '公寓', land: '土地', commercial: '商业', retail: '零售',
    sale: '出售', rental: '出租', search_city: '搜索城市...',
    view: '查看', no_properties: '没有符合条件的房产',
    price: '价格', location: '位置', bedrooms: '卧室', bathrooms: '浴室',
    size: '面积', square_meters: '平方米', status: '状态',
    property_type: '房产类型', listing_type: '列表类型',

    footer_desc: '泰国优质房地产投资平台，提供房产收购、法律服务和财富保护的专业指导。',
    quick_links: '快速链接', locations: '地点', contact: '联系方式',
    register_for_updates: '注册获取更新',
    all_rights: '版权所有。',

    contact_us: '联系我们', line_pook: 'LINE — Pook',
    interested_msg: '对这个房产感兴趣？联系NOK Thailand获取更多信息。',
    description: '描述', amenities: '设施',

    our_professionals: '我们的专业人员', meet_our_team_title: '认识我们的团队',
    team_desc: '致力于为泰国投资者和退休人员提供全面的房地产、法律和金融服务的专业人士。',
    credentials: '资质', expertise: '专长', specialty: '专业',
    line_official: 'LINE官方账号',

    our_services: '我们的服务', services_title: '专业服务',
    services_desc: '为外国投资者和退休人员量身定制的全面房地产、法律、保险和金融服务。',

    knowledge_hub: '知识中心', education_title: '教育与洞察',
    education_desc: '关于泰国房地产、法律事务、保险和投资策略的专家文章和指南。',
    all_topics: '所有主题', read_more: '阅读更多', featured: '精选',
    featured_articles: '精选文章',

    empire_overline: '授权代理',
    empire_title: 'Empire集团泰国',
    empire_desc: '作为Empire集团的授权代理，我们提供独家访问其著名开发项目的机会。',
    interested_empire: '对Empire集团房产感兴趣？',
    empire_cta_desc: '作为授权代理，我们可以提供独家访问最新开发项目、特别定价和个性化咨询。',
    contact_our_team: '联系我们的团队', visit_empire: '访问Empire集团网站',

    media_gallery: '媒体画廊', media_desc: '房产发布会、团队活动和精选房源的照片和亮点。',

    translate_thai: 'ไทย', translate_chinese: '中文', translate_russian: 'Русский',
  },
  ru: {
    properties: 'Недвижимость', services: 'Услуги', education: 'Обучение',
    media: 'Медиа', our_team: 'Наша команда', empire_group: 'Empire Group',
    sign_in: 'Войти', register: 'Регистрация', admin_access: 'Админ',
    back: 'Назад', back_to_home: 'На главную', back_to_listings: 'К списку',
    logout: 'Выйти',

    hero_overline: '01 — Добро пожаловать',
    hero_title_1: 'Премиальная недвижимость', hero_title_2: 'Инвестиции',
    hero_subtitle: 'Продажа жилья, аренда люкс, приобретение земли и партнёрство в гостиничном бизнесе по всему Таиланду.',
    explore_properties: 'Смотреть недвижимость', meet_our_team: 'Наша команда',

    stat_properties: 'Объектов недвижимости', stat_cities: 'Ключевых локаций',
    stat_clients: 'Довольных клиентов', stat_experience: 'Лет опыта',

    featured_overline: '02 — Избранные объекты', featured_title: 'Эксклюзивная недвижимость',

    about_overline: '03 — О NOK',
    about_title: 'Ваш надёжный партнёр в сфере недвижимости Таиланда',
    about_desc: 'Мы предоставляем экспертные консультации по приобретению недвижимости, юридическим услугам, страхованию и защите активов.',
    about_item_1: 'Продажа жилья и аренда люкс', about_item_2: 'Приобретение земли и строительство',
    about_item_3: 'Страхование и защита активов', about_item_4: 'Юридические консультации для иностранных инвесторов',
    view_our_services: 'Наши услуги',

    all_properties_overline: '04 — Все объекты', browse_collection: 'Просмотр коллекции',
    all_types: 'Все типы', all_listings: 'Все объявления',
    house: 'Дом', condo: 'Кондо', land: 'Земля', commercial: 'Коммерческая', retail: 'Торговая',
    sale: 'Продажа', rental: 'Аренда', search_city: 'Поиск города...',
    view: 'Смотреть', no_properties: 'Нет подходящей недвижимости',
    price: 'Цена', location: 'Расположение', bedrooms: 'Спальни', bathrooms: 'Ванные',
    size: 'Площадь', square_meters: 'кв.м.', status: 'Статус',
    property_type: 'Тип недвижимости', listing_type: 'Тип объявления',

    footer_desc: 'Премиальная платформа недвижимости и инвестиций в Таиланде.',
    quick_links: 'Быстрые ссылки', locations: 'Расположение', contact: 'Контакты',
    register_for_updates: 'Подписаться на обновления',
    all_rights: 'Все права защищены.',

    contact_us: 'Связаться с нами', line_pook: 'LINE — Pook',
    interested_msg: 'Заинтересовала эта недвижимость? Свяжитесь с NOK Thailand для подробностей.',
    description: 'Описание', amenities: 'Удобства',

    our_professionals: 'Наши специалисты', meet_our_team_title: 'Наша команда',
    team_desc: 'Профессионалы, предоставляющие комплексные услуги в сфере недвижимости, права и финансов для инвесторов и пенсионеров в Таиланде.',
    credentials: 'Квалификация', expertise: 'Экспертиза', specialty: 'Специализация',
    line_official: 'LINE Официальный аккаунт',

    our_services: 'Наши услуги', services_title: 'Профессиональные услуги',
    services_desc: 'Комплексные услуги в сфере недвижимости, права, страхования и финансов для иностранных инвесторов.',

    knowledge_hub: 'База знаний', education_title: 'Образование и аналитика',
    education_desc: 'Экспертные статьи и руководства по недвижимости, праву, страхованию и инвестициям в Таиланде.',
    all_topics: 'Все темы', read_more: 'Читать далее', featured: 'Избранное',
    featured_articles: 'Избранные статьи',

    empire_overline: 'Авторизованный агент',
    empire_title: 'Empire Group Таиланд',
    empire_desc: 'Как авторизованный агент Empire Group мы предоставляем эксклюзивный доступ к их престижным проектам.',
    interested_empire: 'Заинтересованы в недвижимости Empire Group?',
    empire_cta_desc: 'Как авторизованный агент мы обеспечиваем эксклюзивный доступ к новейшим проектам, специальные цены и персональные консультации.',
    contact_our_team: 'Связаться с командой', visit_empire: 'Посетить сайт Empire Group',

    media_gallery: 'Медиа галерея', media_desc: 'Фотографии и события наших презентаций, мероприятий команды и избранных объектов.',

    translate_thai: 'ไทย', translate_chinese: '中文', translate_russian: 'Русский',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const savedLang = localStorage.getItem('preferred_language') || 'en';
    setLanguage(savedLang);
  }, []);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('preferred_language', lang);
  };

  const translate = async (text) => {
    if (language === 'en' || !text) return text;
    try {
      const langMap = { th: 'th', zh: 'zh-cn', ru: 'ru' };
      const response = await axios.post(`${API}/translate`, {
        text,
        target_language: langMap[language] || language
      });
      return response.data.translated;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  };

  const t = (key, fallback) => {
    return translations[language]?.[key] || translations.en?.[key] || fallback || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, translate, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
